import { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router'
import queryString from 'query-string'

import { getDefaultFinishedOrdersPeriod, isValidFinishedPeriodDate } from './order-list-finished-period.utils'

export const useOrderListFinishedPeriodLocation = () => {
  const location = useLocation()
  const history = useHistory()
  const queryObject = useMemo(() => queryString.parse(location.search), [location.search])

  const getFromUrl = useCallback(() => {
    const rawDateFrom = Array.isArray(queryObject.dateFrom) ? queryObject.dateFrom[0] : queryObject.dateFrom
    const rawDateTo = Array.isArray(queryObject.dateTo) ? queryObject.dateTo[0] : queryObject.dateTo

    const dateFrom = typeof rawDateFrom === 'string' && isValidFinishedPeriodDate(rawDateFrom) ? rawDateFrom : ''
    const dateTo = typeof rawDateTo === 'string' && isValidFinishedPeriodDate(rawDateTo) ? rawDateTo : ''

    return { dateFrom, dateTo }
  }, [queryObject.dateFrom, queryObject.dateTo])

  const setPeriod = useCallback(
    (dateFrom: string, dateTo: string) => {
      const searchParams = new URLSearchParams(history.location.search)

      if (dateFrom) {
        searchParams.set('dateFrom', dateFrom)
      } else {
        searchParams.delete('dateFrom')
      }

      if (dateTo) {
        searchParams.set('dateTo', dateTo)
      } else {
        searchParams.delete('dateTo')
      }

      history.push({ search: searchParams.toString() })
    },
    [history],
  )

  const ensureDefaultInUrl = useCallback(() => {
    const searchParams = new URLSearchParams(history.location.search)
    const defaults = getDefaultFinishedOrdersPeriod()
    let changed = false

    const dateFrom = searchParams.get('dateFrom') || defaults.dateFrom
    const dateTo = searchParams.get('dateTo') || defaults.dateTo

    if (!searchParams.get('dateFrom')) {
      searchParams.set('dateFrom', dateFrom)
      changed = true
    }

    if (!searchParams.get('dateTo')) {
      searchParams.set('dateTo', dateTo)
      changed = true
    }

    if (changed) {
      history.replace({ search: searchParams.toString() })
    }

    return {
      dateFrom: isValidFinishedPeriodDate(dateFrom) ? dateFrom : defaults.dateFrom,
      dateTo: isValidFinishedPeriodDate(dateTo) ? dateTo : defaults.dateTo,
    }
  }, [history])

  const clearFromUrl = useCallback(() => {
    const searchParams = new URLSearchParams(history.location.search)

    if (!searchParams.has('dateFrom') && !searchParams.has('dateTo')) return

    searchParams.delete('dateFrom')
    searchParams.delete('dateTo')
    history.push({ search: searchParams.toString() })
  }, [history])

  return useMemo(() => ({ getFromUrl, setPeriod, ensureDefaultInUrl, clearFromUrl }), [getFromUrl, setPeriod, ensureDefaultInUrl, clearFromUrl])
}
